import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-mint-green hover:text-mint-green/80 font-body text-sm mb-6 transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          <div className="bg-white border-2 border-soft-gray/50 rounded-2xl p-8 shadow-md">
            {sent ? (
              <div className="text-center" data-testid="reset-email-sent">
                <div className="w-16 h-16 bg-mint-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-mint-green" />
                </div>
                <h1 className="font-display text-2xl text-text-dark mb-3">Check your email</h1>
                <p className="font-body text-text-medium mb-6">
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                  It will expire in 1 hour.
                </p>
                <p className="font-body text-sm text-text-medium/70">
                  Didn't receive it? Check your spam folder, or{' '}
                  <button
                    onClick={() => { setSent(false); setEmail(''); }}
                    className="text-mint-green hover:underline font-semibold"
                  >
                    try again
                  </button>.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-sunny-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-sunny-yellow" />
                  </div>
                  <h1 className="font-display text-2xl text-text-dark mb-2">Forgot your password?</h1>
                  <p className="font-body text-sm text-text-medium">
                    Enter your email and we'll send you a link to reset it.
                  </p>
                </div>

                {error && (
                  <div className="bg-coral-orange/10 border border-coral-orange/30 rounded-lg p-3 mb-4 text-coral-orange font-body text-sm text-center" data-testid="forgot-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                  <div>
                    <label className="block font-body font-semibold text-sm text-text-dark mb-2">Email address</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-cream border-2 border-soft-gray/50 rounded-xl text-text-dark font-body focus:outline-none focus:border-mint-green focus:ring-2 focus:ring-mint-green/20"
                      placeholder="your@email.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green text-white font-display text-lg rounded-xl transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
